import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, FileText, Video, Calendar, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const ContentSection = () => {
  const { contentData } = useLanguage();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      id="content" 
      className="py-20 md:py-32"
      style={{ background: 'var(--bg-section)' }}
    >
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen size={24} style={{ color: 'var(--accent-blue-400)' }} />
          </div>
          <h2 className="heading-hero mb-4">{contentData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            {contentData.subtitle}
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="max-w-4xl mx-auto">
          {/* Articles Column */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-6">
              <FileText size={18} style={{ color: 'var(--text-primary)' }} />
              <h3 className="heading-3">Artigos Recentes</h3>
            </div>

            <div className="space-y-4">
              {contentData.articles.map((article) => (
                <motion.div
                  key={article.id}
                  variants={itemVariants}
                  className="voice-card hover-lift cursor-pointer group"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="btn-tag" style={{ padding: '0.25rem 0.5rem', fontSize: '0.625rem' }}>
                          {article.category}
                        </span>
                        <span className="caption flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(article.date)}
                        </span>
                      </div>
                      <h4 className="voice-card-title group-hover:opacity-80 transition-opacity">
                        {article.title}
                      </h4>
                      <p className="voice-card-description mt-2">
                        {article.excerpt}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center group-hover:bg-[var(--text-primary)] group-hover:text-white transition-all duration-200"
                        style={{ background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}
                      >
                        <ArrowRight size={16} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContentSection;
